// All site content lives in six Firestore documents under the `content`
// collection. One doc per section (rather than one doc per match) keeps a page
// load at 6 reads instead of ~250 — comfortably inside the free Spark quota —
// and the whole dataset is well under Firestore's 1 MiB per-document limit.
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db, isConfigured } from '../firebase';
import seed from '../data/seed.json';

export const SECTIONS = ['karte', 'boce', 'reports', 'players', 'gallery', 'settings'];

// Sections stored as { items: [...] }; `settings` is stored as a plain object.
const isList = (section) => section !== 'settings';

export const emptyContent = () => ({
  karte: [], boce: [], reports: [], players: [], gallery: [], settings: seed.settings,
});

export const seedContent = () => JSON.parse(JSON.stringify(seed));

const unwrap = (section, data) => {
  if (!data) return isList(section) ? [] : seed.settings;
  return isList(section) ? data.items || [] : data;
};

/**
 * Subscribe to every section.
 *
 * Each section settles independently — on success with its data, on failure
 * with an error — and the callback fires once all six have settled. A refused
 * read must never look like an empty database, so errors are reported rather
 * than swallowed.
 */
export const subscribeContent = (onData, onError) => {
  if (!isConfigured) {
    onData(seedContent(), 'seed', {});
    return () => {};
  }

  const current = emptyContent();
  const errors = {};
  const settled = new Set();

  const emit = () => {
    if (settled.size !== SECTIONS.length) return;
    onData({ ...current }, 'firestore', { ...errors });
    const first = Object.values(errors)[0];
    if (first && onError) onError(first);
  };

  const unsubs = SECTIONS.map((section) =>
    onSnapshot(
      doc(db, 'content', section),
      (snap) => {
        current[section] = unwrap(section, snap.exists() ? snap.data() : null);
        delete errors[section];
        settled.add(section);
        emit();
      },
      (err) => {
        console.error(`Firestore: ne mogu učitati "${section}"`, err);
        errors[section] = { code: err.code || 'unknown', message: err.message };
        settled.add(section);
        emit();
      }
    )
  );

  return () => unsubs.forEach((u) => u());
};

/** Overwrite one section. Used by the admin panel. */
export const saveSection = async (section, value) => {
  if (!isConfigured) throw new Error('Firebase nije konfiguriran (provjeri .env.local).');
  const payload = isList(section) ? { items: value } : value;
  await setDoc(doc(db, 'content', section), payload);
};

/**
 * Records present in the bundled seed file but not yet in the database,
 * matched by id. Lets a new batch of results be added without touching
 * anything already stored.
 */
export const pendingImports = (content) => {
  const seed = seedContent();
  const out = {};
  SECTIONS.filter(isList).forEach((section) => {
    const have = new Set((content[section] || []).map((x) => x.id));
    const missing = (seed[section] || []).filter((x) => x.id && !have.has(x.id));
    if (missing.length) out[section] = missing;
  });
  return out;
};

export const countPending = (content) =>
  Object.values(pendingImports(content)).reduce((n, list) => n + list.length, 0);

/** Appends only the missing records. Never overwrites or deletes. */
export const importMissing = async (content) => {
  const pending = pendingImports(content);
  for (const [section, items] of Object.entries(pending)) {
    // eslint-disable-next-line no-await-in-loop
    await saveSection(section, [...(content[section] || []), ...items]);
  }
  return pending;
};
